import { useMutation } from "@tanstack/react-query";
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore";
import app from "../../../firebase/firebase";
import JSZip from "jszip";
import { message } from "antd";

const db = getFirestore(app);

export const downloadAllCV = async (jobId: string): Promise<Blob> => {
  const appsRef = collection(db, "applications");
  const q = query(appsRef, where("jobId", "==", jobId));
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    throw new Error("No applications with resumes found for this job.");
  }

  const zip = new JSZip();
  const folder = zip.folder(`resumes_${jobId}`)!;
  const usedFilenames = new Set<string>();

  const fetchPromises = snapshot.docs.map(async (doc) => {
    const appData = doc.data();
    const resumeUrl = appData.resume as string;

    if (!resumeUrl) return;

    // ---- 👇 Applicant name-based filename ----
    const applicantInfo = appData.applicant_info;
    let baseName =
      applicantInfo?.firstName && applicantInfo?.lastName
        ? `${applicantInfo.firstName}_${applicantInfo.lastName}`
        : `resume_${doc.id}`;

    // Sanitize invalid filename characters
    baseName = baseName.replace(/[<>:"/\\|?*]+/g, "_");

    // ---- 👇 Robust file extension detection ----
    const pathname = new URL(resumeUrl).pathname;
    const lastPart = pathname.split("/").pop() || "";
    const hasDot = lastPart.includes(".");
    const fileExtension = hasDot ? lastPart.split(".").pop()! : "pdf";

    // ---- 👇 Ensure unique filenames in case of duplicates ----
    let filename = `${baseName}.${fileExtension}`;
    let counter = 1;
    while (usedFilenames.has(filename)) {
      filename = `${baseName}(${counter}).${fileExtension}`;
      counter++;
    }
    usedFilenames.add(filename);

    try {
      const response = await fetch(resumeUrl);
      if (!response.ok) {
        console.warn(`Failed to fetch ${filename}: ${response.statusText}`);
        return;
      }
      const blob = await response.blob();
      folder.file(filename, blob);
    } catch (error) {
      console.error(`Error fetching ${filename}:`, error);
    }
  });

  await Promise.all(fetchPromises);

  return zip.generateAsync({ type: "blob" });
};

/**
 * Hook to download all resumes for a jobId with user feedback.
 */
export const useGetAllResume = () => {
  return useMutation<Blob, Error, string>({
    mutationFn: downloadAllCV,
    onSuccess: (zipBlob, jobId) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(zipBlob);
      link.download = `resumes_${jobId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success("Resumes ZIP download started!");
    },
    onError: (error) => {
      console.error("Error creating ZIP:", error);
      message.error(error.message || "Could not download resumes.");
    },
  });
};
